import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Popover,
  Stack,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { buildStoreUrl } from '../../../utils/storeUrl.ts';
import {
  IconBrowser,
  IconClipboardCheck,
  IconClock,
  IconCreditCard,
  IconEdit,
  IconPhoto,
  IconTrash,
} from '@tabler/icons-react';
import {
  getServiceStatusColor,
  getServiceStatusLabel,
} from '../../../const/service_status_mapping.ts';
import { dayMap } from '../../../utils/DateUtil.ts';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import React from 'react';
import useInspectionService from '../../../hooks/inspection-services/useInspectionService.ts';
import useBrand from '../../../hooks/brands/useBrand.ts';
import { modals } from '@mantine/modals';
import { ServiceType } from '../../../models/ServiceType.ts';
import { useQueries } from '@tanstack/react-query';
import { getTag } from '../../../api/tag.ts';
import { getPaymentMethod } from '../../../api/payment-method.ts';
import { useTheme } from '../../../context/ThemeProvider.tsx';

const phoneUtil = PhoneNumberUtil.getInstance();

interface InspectionServiceInformationCardProps {
  serviceId: string;
  setIsServiceImageCarouselOpen: (isServiceImageCarouselOpen: boolean) => void;
  setSelectedServiceIdForCarousel: (
    selectedServiceIdForCarousel: string | null,
  ) => void;
  setSelectedServiceTypeForCarousel: (
    selectedServiceTypeForCarousel: ServiceType | null,
  ) => void;
  handleEditInspectionServiceMetadataClick: (serviceId: string) => void;
  handleDeleteInspectionServiceClick: (serviceId: string) => void;
  handleUpdateInspectionServiceHeaderInfoClick: (serviceId: string) => void;
}

const InspectionServiceInformationCard: React.FC<
  InspectionServiceInformationCardProps
> = ({
  serviceId,
  setIsServiceImageCarouselOpen,
  setSelectedServiceIdForCarousel,
  setSelectedServiceTypeForCarousel,
  handleEditInspectionServiceMetadataClick,
  handleDeleteInspectionServiceClick,
  handleUpdateInspectionServiceHeaderInfoClick,
}) => {
  const { data: service, isLoading: isInspectionServiceLoading } =
    useInspectionService(serviceId);
  const { data: brand, isLoading: isBrandLoading } = useBrand(service?.brandId);

  const tagQueries = useQueries({
    queries: (service?.tags ?? []).map((tagId) => ({
      queryKey: ['tags', tagId],
      queryFn: () => getTag(tagId),
      enabled: !!tagId,
    })),
  });
  const tagNames = tagQueries.map((q) => q.data?.name).filter(Boolean);
  const isTagsLoading = tagQueries.some((q) => q.isLoading);

  const paymentMethodQueries = useQueries({
    queries: (service?.paymentMethods ?? []).map((paymentMethodId) => ({
      queryKey: ['paymentMethods', paymentMethodId],
      queryFn: () => getPaymentMethod(paymentMethodId),
      enabled: !!paymentMethodId,
    })),
  });
  const paymentMethodNames = paymentMethodQueries
    .map((q) => q.data?.name)
    .filter(Boolean);
  const isPaymentMethodsLoading = paymentMethodQueries.some((q) => q.isLoading);

  const { colorScheme } = useTheme();

  if (
    isBrandLoading ||
    isInspectionServiceLoading ||
    isTagsLoading ||
    isPaymentMethodsLoading
  )
    return <LoadingOverlay />;

  return (
    <>
      <Paper
        key={serviceId}
        shadow="xs"
        style={{ height: 588, borderRadius: 16, marginBottom: 20 }}
      >
        <Group
          style={{
            justifyContent: 'space-between',
            paddingInline: 30,
            paddingTop: 38,
            paddingBottom: 19,
          }}
        >
          <Group gap={18}>
            <ThemeIcon
              style={{
                width: 62,
                height: 62,
                backgroundColor: '#15AABF',
                borderRadius: '50%',
              }}
            >
              <IconClipboardCheck style={{ width: 40, height: 40 }} />
            </ThemeIcon>

            <Stack gap={10}>
              <Group>
                <Text
                  style={{
                    lineHeight: '32px',
                    fontSize: 24,
                    fontWeight: 700,
                  }}
                >
                  {service.name}
                </Text>
                <ActionIcon size={'sm'} variant={'light'}>
                  <IconEdit
                    style={{ width: 20, height: 20 }}
                    onClick={() => {
                      handleUpdateInspectionServiceHeaderInfoClick(service.id);
                    }}
                  />
                </ActionIcon>
              </Group>

              <Group gap={10}>
                {/* 服務狀態 */}
                <Badge color={getServiceStatusColor(service.serviceStatus)}>
                  {getServiceStatusLabel(service.serviceStatus)}
                </Badge>
                {/* 標籤 */}
                {tagNames.map((tag) => (
                  <Badge variant={'outline'}>{tag}</Badge>
                ))}
              </Group>
            </Stack>
          </Group>

          <ActionIcon size={'lg'} color={'red'} variant={'light'}>
            <IconTrash
              style={{ width: 20, height: 20 }}
              onClick={() =>
                modals.openConfirmModal({
                  title: (
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      刪除驗車服務
                    </Text>
                  ),
                  children: (
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                      }}
                    >
                      是否確認刪除驗車服務？
                    </Text>
                  ),
                  centered: true,
                  labels: {
                    confirm: '確認',
                    cancel: '取消',
                  },
                  confirmProps: { color: 'blue' },

                  onConfirm: async () => {
                    handleDeleteInspectionServiceClick(serviceId);
                  },
                })
              }
            />
          </ActionIcon>
        </Group>

        <div
          style={{
            height: 1,
            backgroundColor: colorScheme === 'light' ? '#dee2e6' : '#737373ff',
            marginInline: 30,
          }}
        />

        <div
          style={{
            position: 'relative',
            display: 'flex',
            height: 410,
            paddingInline: 30,
            marginTop: 20,
          }}
        >
          <div style={{ flex: 1, height: 410, minWidth: 0 }}>
            <Group
              justify={'space-between'}
              style={{ height: 40, marginBottom: 22 }}
            >
              <Text style={{ fontSize: 20, fontWeight: 400 }}>服務資訊</Text>
              <Button
                variant={'light'}
                leftSection={<IconEdit />}
                onClick={() =>
                  handleEditInspectionServiceMetadataClick(serviceId)
                }
              >
                編輯
              </Button>
            </Group>

            <Group gap={16} style={{ marginBottom: 20 }}>
              <Button
                onClick={() => {
                  setSelectedServiceIdForCarousel(serviceId);
                  setSelectedServiceTypeForCarousel('INSPECTION');
                  setIsServiceImageCarouselOpen(true);
                }}
                disabled={service.imageIds.length === 0}
                variant={'outline'}
                leftSection={<IconPhoto style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                服務圖片
              </Button>
              <Button
                component={'a'}
                href={service.website}
                disabled={service.website === ''}
                target={'_blank'}
                variant={'outline'}
                leftSection={<IconBrowser style={{ width: 16, height: 16 }} />}
                radius={'xl'}
                style={{
                  width: 112,
                  height: 32,
                  fontSize: 14,
                  fontWeight: 400,
                }}
                styles={{ section: { marginRight: 8 } }}
              >
                官方網站
              </Button>
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    width: 158,
                    height: 160,
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconClock style={{ width: 16, height: 16 }} />
                    }
                    radius={'xl'}
                    style={{
                      width: 112,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    營業時間
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6}>
                      {Object.entries(dayMap).map(([key, { label }]) => {
                        const schedule =
                          service.operationHours?.weeklySchedule ?? [];
                        const day = schedule.find((w) => w.dayOfWeek === key);
                        const isUnknown = day?.isOpen === 'UNKNOWN';
                        const isOpen = day?.isOpen === 'OPEN';

                        const start =
                          day?.timeSlots?.[0]?.startTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        const end =
                          day?.timeSlots?.[0]?.endTime
                            ?.split(':')
                            .slice(0, 2)
                            .join(':') ?? '--:--';

                        return (
                          <Group key={key} gap={3}>
                            <div>{label}</div>

                            {isOpen ? (
                              <>
                                <div>{start}</div>
                                <div>-</div>
                                <div>{end}</div>
                              </>
                            ) : isUnknown ? (
                              <div style={{ color: 'grey' }}>未知</div>
                            ) : (
                              <div style={{ color: 'grey' }}>休息</div>
                            )}
                          </Group>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
              {/* 可用付款方式按鈕 */}
              <Popover
                withArrow
                styles={{
                  dropdown: {
                    fontSize: 12,
                    lineHeight: '14px',
                  },
                }}
              >
                <Popover.Target>
                  <Button
                    variant={'outline'}
                    leftSection={
                      <IconCreditCard style={{ width: 16, height: 16 }} />
                    }
                    disabled={service.paymentMethods.length === 0}
                    radius={'xl'}
                    style={{
                      width: 140,
                      height: 32,
                      fontSize: 14,
                      fontWeight: 400,
                    }}
                    styles={{ section: { marginRight: 8 } }}
                  >
                    可用付款方式
                  </Button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div>
                    <Stack gap={6} align={'start'}>
                      {paymentMethodNames.map((paymentMethod) => {
                        return (
                          <Text fz={12} fw={400}>
                            {paymentMethod}
                          </Text>
                        );
                      })}
                    </Stack>
                  </div>
                </Popover.Dropdown>
              </Popover>
            </Group>

            <Stack gap={10} style={{ minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  height: 92,
                }}
              >
                {/* 品牌名稱 */}
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      品牌名稱
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {brand?.name ?? '-'}
                    </Text>
                  </Stack>
                </div>
                <div
                  style={{
                    flex: 1,
                    backgroundColor:
                      colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                    height: '100%',
                    borderRadius: 12,
                  }}
                >
                  <Stack
                    gap={8}
                    style={{
                      paddingInline: 24,
                      paddingTop: 16,
                      paddingBottom: 16,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      服務電話
                    </Text>
                    <Text style={{ fontSize: 18, fontWeight: 400 }}>
                      {service.phoneNumber
                        ? (() => {
                            try {
                              const parsed = phoneUtil.parse(
                                `${service.phoneNumber.countryCode}${service.phoneNumber.number}`,
                              );

                              return (
                                '(' +
                                service.phoneNumber.countryCode +
                                ') ' +
                                phoneUtil.format(
                                  parsed,
                                  PhoneNumberFormat.NATIONAL,
                                )
                              );
                            } catch {
                              return '-';
                            }
                          })()
                        : '-'}
                    </Text>
                  </Stack>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                  }}
                >
                  <Group gap={8}>
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#868E96',
                      }}
                    >
                      營運商店
                    </Text>
                  </Group>
                  {service.storeIds.length === 0 ? (
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                      }}
                    >
                      -
                    </Text>
                  ) : (
                    <Group gap={12}>
                      {service.storeIds.map((entry, index) => (
                        <React.Fragment key={`${entry.type}-${entry.value}`}>
                          {index > 0 && (
                            <Text style={{ fontSize: 18, fontWeight: 400 }}>
                              |
                            </Text>
                          )}
                          <Anchor
                            href={buildStoreUrl(entry)}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: 18, fontWeight: 400 }}
                          >
                            {'前往商店 - '}
                            {entry.type === 'PARKINGLOT'
                              ? '停車場'
                              : entry.type === 'GAS_STATION'
                                ? '加油站'
                                : '連鎖店'}
                          </Anchor>
                        </React.Fragment>
                      ))}
                    </Group>
                  )}
                </Stack>
              </div>
              <div
                style={{
                  display: 'flex',
                  height: 92,
                  minWidth: 0,
                  backgroundColor:
                    colorScheme === 'light' ? '#F8F9FA' : '#2E2E2E',
                  borderRadius: 12,
                  overflow: 'hidden',
                }}
              >
                <Stack
                  gap={8}
                  style={{
                    paddingInline: 24,
                    paddingTop: 16,
                    paddingBottom: 16,
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 758,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: 400,
                      color: '#868E96',
                    }}
                  >
                    備註文字
                  </Text>
                  <Tooltip
                    multiline
                    position="top-start"
                    label={
                      <Text style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>
                        {service.notes}
                      </Text>
                    }
                    disabled={!service.notes}
                    styles={{
                      tooltip: {
                        maxWidth: 300,
                      },
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 18,
                        fontWeight: 400,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}
                    >
                      {service.notes
                        ? service.notes.split('\n').length > 1
                          ? `${service.notes.split('\n')[0]} ...`
                          : service.notes
                        : '-'}
                    </Text>
                  </Tooltip>
                </Stack>
              </div>
            </Stack>
          </div>
        </div>
      </Paper>
    </>
  );
};

export default InspectionServiceInformationCard;
